import React from 'react'
import CustomTimeAgo from '@/components/CustomTimeAgo'
import { Topic } from '@/types'
import { convertToGMT } from '@/utils/helper'
import type { ActivityPulseItem } from '../components/ActivityPulseCard/ActivityPulseCard'

/**
 * Spectrum dot colors (gold/coral/violet/cyan — see alertPalette.ts), reused
 * here so each event kind reads consistently with the rest of the app
 * instead of introducing a fourth ad hoc color scale.
 */
const DOT = {
  galleryCreated: '#BE9DFD', // violet
  albumCreated: '#FEA67D', // coral
  photosAdded: '#FFCF7D', // gold — matches the "upload" kind in NotificationRow
} as const

/** Photos added in a later session read as "activity"; same-batch uploads are just noise on top of the creation event. */
const LATER_SESSION_MS = 10 * 60 * 1000

const MAX_ITEMS = 6

function sameCalendarDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
}

function toDate(raw?: string): Date | undefined {
  if (!raw) return undefined
  const d = new Date(convertToGMT(raw))
  return isNaN(d.getTime()) ? undefined : d
}

interface RankedItem extends ActivityPulseItem {
  at: number
}

function pushCreated(out: RankedItem[], topic: Topic, kind: 'gallery' | 'album') {
  const created = toDate(topic.dateCreated)
  if (!created) return
  const label = kind === 'gallery' ? 'Created gallery' : 'Created album'
  const icon = kind === 'gallery' ? '✦' : '🖼'
  const dot = kind === 'gallery' ? DOT.galleryCreated : DOT.albumCreated
  out.push({
    id: `${kind}-created-${topic.topicID}`,
    dotColor: dot,
    text: (
      <>
        {icon}&nbsp; {label} <b style={{ color: 'var(--ds-ink)' }}>{topic.topicName}</b>
      </>
    ),
    timeAgo: <CustomTimeAgo date={topic.dateCreated} />,
    at: created.getTime(),
  })
}

function pushPhotosAdded(out: RankedItem[], topic: Topic) {
  const created = toDate(topic.dateCreated)
  const photos = topic.photos ?? []
  if (!created || !photos.length) return

  let latest: { date: Date; raw: string } | undefined
  for (const photo of photos) {
    const d = toDate(photo.dateCreated)
    if (d && (!latest || d.getTime() > latest.date.getTime())) latest = { date: d, raw: photo.dateCreated }
  }
  if (!latest || latest.date.getTime() - created.getTime() < LATER_SESSION_MS) return

  const count = photos.filter((p) => {
    const d = toDate(p.dateCreated)
    return d && sameCalendarDay(d, latest!.date)
  }).length

  out.push({
    id: `photos-added-${topic.topicID}`,
    dotColor: DOT.photosAdded,
    text: (
      <>
        ↑&nbsp; Added <b style={{ color: 'var(--ds-ink)' }}>{count} photo{count === 1 ? '' : 's'}</b> to{' '}
        <b style={{ color: 'var(--ds-ink)' }}>{topic.topicName}</b>
      </>
    ),
    timeAgo: <CustomTimeAgo date={latest.raw} />,
    at: latest.date.getTime(),
  })
}

/**
 * Builds the profile "Recent Activity" feed entirely from topics already
 * fetched for the wall (galleries + albums) — there's no dedicated activity
 * log endpoint, but topic dateCreated/dateUpdated and per-photo dateCreated
 * are enough to derive real, dated events client-side. Works the same for
 * self and public profiles since it's the same data either way.
 */
export function deriveActivityItems(galleries: Topic[], albums: Topic[]): ActivityPulseItem[] {
  const items: RankedItem[] = []

  for (const g of galleries) {
    pushCreated(items, g, 'gallery')
    pushPhotosAdded(items, g)
  }
  for (const a of albums) {
    pushCreated(items, a, 'album')
    pushPhotosAdded(items, a)
  }

  return items
    .sort((a, b) => b.at - a.at)
    .slice(0, MAX_ITEMS)
    .map(({ at, ...item }) => item)
}
